import React, { useEffect, useMemo, useState } from "react";
import {
  Wrench, Plus, Search, X, Car, Wind, HardHat, MessageCircle, FileDown,
  CheckCircle2, Clock, Trash2, Edit3,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

const INDUSTRIES = [
  { key: "automobile",   label: "Automobile",   icon: Car,      color: "#2563EB" },
  { key: "ac_service",   label: "AC Service",   icon: Wind,     color: "#0891B2" },
  { key: "construction", label: "Construction", icon: HardHat,  color: "#EA580C" },
];

const STATUSES = [
  { key: "open",        label: "Open",        cls: "chip-info" },
  { key: "in_progress", label: "In Progress", cls: "chip-warn" },
  { key: "closed",      label: "Closed",      cls: "chip-neutral" },
  { key: "invoiced",    label: "Invoiced",    cls: "chip-success" },
  { key: "cancelled",   label: "Cancelled",   cls: "chip-danger" },
];

function IndustryChip({ industry }) {
  const i = INDUSTRIES.find((x) => x.key === industry);
  if (!i) return null;
  const Icon = i.icon;
  return (
    <span className="chip chip-neutral text-[11px]" style={{ borderColor: i.color, color: i.color }}>
      <Icon size={11}/> {i.label}
    </span>
  );
}

function StatusChip({ status }) {
  const s = STATUSES.find((x) => x.key === status) || { label: status, cls: "chip-neutral" };
  return <span className={`chip ${s.cls}`}>{s.label}</span>;
}

function IndustryFields({ industry, fields, onChange }) {
  const upd = (k, v) => onChange({ ...fields, [k]: v });
  if (industry === "automobile") {
    return (
      <>
        <div><label className="label-field">Vehicle No</label>
          <input className="input-clean" value={fields.vehicle_no || ""} onChange={(e) => upd("vehicle_no", e.target.value.toUpperCase())} placeholder="MH 12 AB 1234" data-testid="jc-field-vehicle_no"/></div>
        <div><label className="label-field">Make</label>
          <input className="input-clean" value={fields.make || ""} onChange={(e) => upd("make", e.target.value)} data-testid="jc-field-make"/></div>
        <div><label className="label-field">Model</label>
          <input className="input-clean" value={fields.model || ""} onChange={(e) => upd("model", e.target.value)} data-testid="jc-field-model"/></div>
        <div><label className="label-field">Odometer (km)</label>
          <input type="number" className="input-clean" value={fields.odometer_km || ""} onChange={(e) => upd("odometer_km", e.target.value)} data-testid="jc-field-odometer"/></div>
      </>
    );
  }
  if (industry === "ac_service") {
    return (
      <>
        <div><label className="label-field">Unit Type</label>
          <select className="input-clean" value={fields.unit_type || ""} onChange={(e) => upd("unit_type", e.target.value)} data-testid="jc-field-unit_type">
            <option value="">Select…</option>
            <option>Split AC</option><option>Window AC</option><option>Cassette</option>
            <option>Central AC</option><option>VRF</option><option>Chiller</option>
          </select></div>
        <div><label className="label-field">Brand</label>
          <input className="input-clean" value={fields.brand || ""} onChange={(e) => upd("brand", e.target.value)} data-testid="jc-field-brand"/></div>
        <div><label className="label-field">Model</label>
          <input className="input-clean" value={fields.model || ""} onChange={(e) => upd("model", e.target.value)} data-testid="jc-field-model"/></div>
        <div><label className="label-field">Tonnage</label>
          <input className="input-clean" value={fields.tonnage || ""} onChange={(e) => upd("tonnage", e.target.value)} placeholder="1.5 Ton" data-testid="jc-field-tonnage"/></div>
        <div className="md:col-span-2"><label className="label-field">Location</label>
          <input className="input-clean" value={fields.location || ""} onChange={(e) => upd("location", e.target.value)} placeholder="Site / Floor / Room" data-testid="jc-field-location"/></div>
      </>
    );
  }
  return (
    <>
      <div><label className="label-field">Site Name</label>
        <input className="input-clean" value={fields.site_name || ""} onChange={(e) => upd("site_name", e.target.value)} data-testid="jc-field-site_name"/></div>
      <div><label className="label-field">Work Type</label>
        <input className="input-clean" value={fields.work_type || ""} onChange={(e) => upd("work_type", e.target.value)} placeholder="Civil, Electrical, Plumbing…" data-testid="jc-field-work_type"/></div>
      <div className="md:col-span-2"><label className="label-field">Site Address</label>
        <input className="input-clean" value={fields.site_address || ""} onChange={(e) => upd("site_address", e.target.value)} data-testid="jc-field-site_address"/></div>
      <div className="md:col-span-2"><label className="label-field">Scope of Work</label>
        <textarea className="input-clean" rows={2} value={fields.scope || ""} onChange={(e) => upd("scope", e.target.value)} data-testid="jc-field-scope"/></div>
    </>
  );
}

function JobCardModal({ open, initial, onClose, onSaved }) {
  const { active } = useCompany();
  const [parties, setParties] = useState([]);
  const [f, setF] = useState({
    industry: "automobile", party_id: "",
    job_date: new Date().toISOString().slice(0, 10),
    promised_date: "", technician: "", complaint: "", diagnosis: "", notes: "",
    fields: {}, parts: [], labor: [], status: "open",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(initial ? {
      industry: initial.industry, party_id: initial.party_id,
      job_date: initial.job_date, promised_date: initial.promised_date || "",
      technician: initial.technician || "", complaint: initial.complaint || "",
      diagnosis: initial.diagnosis || "", notes: initial.notes || "",
      fields: initial.fields || {}, parts: initial.parts || [], labor: initial.labor || [],
      status: initial.status || "open",
    } : {
      industry: "automobile", party_id: "",
      job_date: new Date().toISOString().slice(0, 10),
      promised_date: "", technician: "", complaint: "", diagnosis: "", notes: "",
      fields: {}, parts: [], labor: [], status: "open",
    });
    (async () => {
      try {
        const { data } = await api.get("/parties?type=customer&limit=500");
        setParties(data.parties || []);
      } catch { /* ignore */ }
    })();
  }, [open, initial]);

  const addPart = () => setF((s) => ({ ...s, parts: [...s.parts, { name: "", qty: 1, unit_price: 0, tax_rate: 18 }] }));
  const rmPart = (i) => setF((s) => ({ ...s, parts: s.parts.filter((_, x) => x !== i) }));
  const updPart = (i, patch) => setF((s) => ({ ...s, parts: s.parts.map((p, x) => x === i ? { ...p, ...patch } : p) }));

  const addLabor = () => setF((s) => ({ ...s, labor: [...s.labor, { description: "", hours: 1, rate: 0, tax_rate: 18 }] }));
  const rmLabor = (i) => setF((s) => ({ ...s, labor: s.labor.filter((_, x) => x !== i) }));
  const updLabor = (i, patch) => setF((s) => ({ ...s, labor: s.labor.map((l, x) => x === i ? { ...l, ...patch } : l) }));

  const totals = useMemo(() => {
    const p = f.parts.reduce((s, x) => s + Number(x.qty || 0) * Number(x.unit_price || 0), 0);
    const l = f.labor.reduce((s, x) => s + Number(x.hours || 0) * Number(x.rate || 0), 0);
    return { parts: p, labor: l, total: p + l };
  }, [f.parts, f.labor]);

  if (!open) return null;

  const submit = async () => {
    if (!f.party_id) { toast.error("Select a customer"); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        parts: f.parts.map((p) => ({ ...p, qty: Number(p.qty || 0), unit_price: Number(p.unit_price || 0), tax_rate: Number(p.tax_rate || 0) })),
        labor: f.labor.map((l) => ({ ...l, hours: Number(l.hours || 0), rate: Number(l.rate || 0), tax_rate: Number(l.tax_rate || 0) })),
      };
      if (initial) await api.put(`/job-cards/${initial.id}`, body);
      else await api.post("/job-cards", body);
      toast.success(initial ? "Job card updated" : "Job card created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="jc-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl p-4 sm:p-6 my-4 sm:my-0 sm:max-h-[92vh] sm:overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4 sticky top-0 bg-white pt-1 -mt-1 z-10">
          <div>
            <div className="overline mb-1">Job Card</div>
            <h2 className="text-lg sm:text-xl font-bold text-[#0F172A]">
              {initial ? `Edit ${initial.number}` : "New Job Card"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="jc-close" className="p-2 -mr-2" style={{ minWidth: 44, minHeight: 44 }}><X size={18}/></button>
        </div>

        {/* Industry pills — horizontal scroll on mobile so all 3 stay one-tap */}
        <div className="flex gap-2 mb-4 overflow-x-auto -mx-1 px-1 pb-1">
          {INDUSTRIES.map((i) => {
            const Icon = i.icon;
            const on = f.industry === i.key;
            return (
              <button key={i.key} data-testid={`jc-industry-${i.key}`}
                onClick={() => setF((s) => ({ ...s, industry: i.key, fields: {} }))}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg border font-semibold text-sm transition-all shrink-0 ${on ? "bg-[#FFEDE0] border-[#FF5500] text-[#FF5500]" : "border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]"}`}>
                <Icon size={14}/> {i.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label-field">Customer *</label>
            <select className="input-clean" value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })} data-testid="jc-party">
              <option value="">Select customer…</option>
              {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.phone ? ` · ${p.phone}` : ""}</option>)}
            </select></div>
          <div><label className="label-field">Technician</label>
            <input className="input-clean" value={f.technician} onChange={(e) => setF({ ...f, technician: e.target.value })} data-testid="jc-technician"/></div>
          <div><label className="label-field">Job Date *</label>
            <input type="date" className="input-clean" value={f.job_date} onChange={(e) => setF({ ...f, job_date: e.target.value })} data-testid="jc-date"/></div>
          <div><label className="label-field">Promised Date</label>
            <input type="date" className="input-clean" value={f.promised_date} onChange={(e) => setF({ ...f, promised_date: e.target.value })}/></div>

          {/* Industry fields */}
          <IndustryFields industry={f.industry} fields={f.fields} onChange={(v) => setF({ ...f, fields: v })}/>

          <div className="md:col-span-2"><label className="label-field">Complaint</label>
            <textarea className="input-clean" rows={2} value={f.complaint} onChange={(e) => setF({ ...f, complaint: e.target.value })} data-testid="jc-complaint"/></div>
          <div className="md:col-span-2"><label className="label-field">Diagnosis / Work Done</label>
            <textarea className="input-clean" rows={2} value={f.diagnosis} onChange={(e) => setF({ ...f, diagnosis: e.target.value })}/></div>
        </div>

        {/* Parts */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">Parts</div>
            <button className="btn-ghost text-xs" onClick={addPart} data-testid="jc-add-part">
              <Plus size={12}/> Add Part
            </button>
          </div>
          {f.parts.length === 0 && <div className="text-sm text-[#94A3B8] italic">No parts added.</div>}
          {f.parts.map((p, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 mb-3 pb-3 border-b sm:border-none border-[#E2E8F0]" data-testid={`jc-part-row-${i}`}>
              <div className="col-span-2 sm:col-span-5"><label className="label-field sm:hidden">Part</label><input className="input-clean" placeholder="Part name" value={p.name} onChange={(e) => updPart(i, { name: e.target.value })}/></div>
              <div className="sm:col-span-2"><label className="label-field sm:hidden">Qty</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Qty" value={p.qty} onChange={(e) => updPart(i, { qty: e.target.value })}/></div>
              <div className="sm:col-span-2"><label className="label-field sm:hidden">Unit ₹</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Unit ₹" value={p.unit_price} onChange={(e) => updPart(i, { unit_price: e.target.value })}/></div>
              <div className="sm:col-span-2"><label className="label-field sm:hidden">Tax %</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Tax %" value={p.tax_rate} onChange={(e) => updPart(i, { tax_rate: e.target.value })}/></div>
              <div className="col-span-2 sm:col-span-1 flex sm:justify-center items-center"><button className="text-[#DC2626] p-2" onClick={() => rmPart(i)} aria-label="Remove part"><Trash2 size={14}/></button></div>
            </div>
          ))}
        </div>

        {/* Labor */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">Labor</div>
            <button className="btn-ghost text-xs" onClick={addLabor} data-testid="jc-add-labor">
              <Plus size={12}/> Add Labor
            </button>
          </div>
          {f.labor.length === 0 && <div className="text-sm text-[#94A3B8] italic">No labor lines.</div>}
          {f.labor.map((l, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 mb-3 pb-3 border-b sm:border-none border-[#E2E8F0]" data-testid={`jc-labor-row-${i}`}>
              <div className="col-span-2 sm:col-span-5"><label className="label-field sm:hidden">Description</label><input className="input-clean" placeholder="Description" value={l.description} onChange={(e) => updLabor(i, { description: e.target.value })}/></div>
              <div className="sm:col-span-2"><label className="label-field sm:hidden">Hours</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Hours" value={l.hours} onChange={(e) => updLabor(i, { hours: e.target.value })}/></div>
              <div className="sm:col-span-2"><label className="label-field sm:hidden">Rate ₹</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Rate ₹" value={l.rate} onChange={(e) => updLabor(i, { rate: e.target.value })}/></div>
              <div className="sm:col-span-2"><label className="label-field sm:hidden">Tax %</label><input type="number" inputMode="decimal" className="input-clean" placeholder="Tax %" value={l.tax_rate} onChange={(e) => updLabor(i, { tax_rate: e.target.value })}/></div>
              <div className="col-span-2 sm:col-span-1 flex sm:justify-center items-center"><button className="text-[#DC2626] p-2" onClick={() => rmLabor(i)} aria-label="Remove labor"><Trash2 size={14}/></button></div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-full sm:w-64 space-y-1 text-sm font-mono-data">
            <div className="flex justify-between text-[#64748B]"><span>Parts</span><span>{fmtMoney(totals.parts, active?.base_currency || "INR")}</span></div>
            <div className="flex justify-between text-[#64748B]"><span>Labor</span><span>{fmtMoney(totals.labor, active?.base_currency || "INR")}</span></div>
            <div className="flex justify-between font-bold text-[#0F172A] pt-1 border-t border-[#E2E8F0]"><span>Estimate (ex-tax)</span><span data-testid="jc-total">{fmtMoney(totals.total, active?.base_currency || "INR")}</span></div>
          </div>
        </div>

        <div className="mt-3"><label className="label-field">Internal Notes</label>
          <textarea className="input-clean" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-5 sticky bottom-0 bg-white pt-3 -mx-4 sm:mx-0 px-4 sm:px-0 border-t sm:border-t-0 border-[#E2E8F0]">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button className="btn-primary w-full sm:w-auto justify-center" onClick={submit} disabled={saving} data-testid="jc-save">
            {saving ? "Saving…" : (initial ? "Save Changes" : "Create Job Card")}
          </button>
        </div>
      </div>
    </div>
  );
}

function waLink(phone, message) {
  const num = (phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
}

export default function JobCards() {
  const { active } = useCompany();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industry) params.set("industry", industry);
      if (status) params.set("status", status);
      const { data } = await api.get(`/job-cards?${params}`);
      setRows(data.job_cards || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, industry, status]);

  const filtered = rows.filter((r) => !q ||
    (r.number || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.party_snapshot?.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.fields?.vehicle_no || "").toLowerCase().includes(q.toLowerCase())
  );

  const closeCard = async (jc) => {
    if (!window.confirm(`Close ${jc.number} and auto-generate a draft invoice?`)) return;
    try {
      const { data } = await api.post(`/job-cards/${jc.id}/close`);
      toast.success(`Draft invoice ${data.invoice_number} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (jc) => {
    if (!window.confirm(`Delete ${jc.number}?`)) return;
    try {
      await api.delete(`/job-cards/${jc.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async (jc) => {
    try {
      const res = await api.get(`/job-cards/${jc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(jc.number || "job-card").replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF generation failed");
    }
  };
  const sendWhatsApp = (jc) => {
    const phone = jc.party_snapshot?.phone;
    if (!phone) { toast.error("Customer has no phone number"); return; }
    const msg = `Hi ${jc.party_snapshot.name}, your service Job Card ${jc.number} is ready. Total: ${fmtMoney(jc.grand_total, active?.base_currency || "INR")}. Regards, ${active?.name || "SERP"}`;
    window.open(waLink(phone, msg), "_blank");
  };

  return (
    <div className="space-y-4" data-testid="job-cards-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Service Management</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Wrench className="text-[#FF5500]" size={24}/> Job Cards
          </h1>
          <div className="text-sm text-[#64748B] mt-1">
            Track service work orders across Automobile, AC Service, and Construction industries.
          </div>
        </div>
        <button className="btn-primary w-full sm:w-auto justify-center" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="new-job-card-btn">
          <Plus size={14}/> New Job Card
        </button>
      </div>

      {/* Filter pills */}
      <div className="card-surface p-4">
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]"/>
            <input className="input-clean pl-7" placeholder="Search number / customer / vehicle" value={q} onChange={(e) => setQ(e.target.value)} data-testid="jc-search"/>
          </div>
          <select className="input-clean w-full sm:w-[180px]" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="jc-industry-filter">
            <option value="">All industries</option>
            {INDUSTRIES.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
          </select>
          <select className="input-clean w-full sm:w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="jc-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="card-surface p-4">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Wrench size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B] text-sm">
              {loading ? "Loading…" : q ? "No matching job cards." : "No job cards yet."}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile card layout — visible < md */}
            <div className="md:hidden space-y-3">
              {filtered.map((jc) => (
                <div key={jc.id} data-testid={`jc-card-${jc.id}`} className="border border-[#E2E8F0] rounded-lg p-3 bg-white">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-mono-data font-semibold text-[#0F172A] text-sm">{jc.number}</div>
                      <div className="text-sm truncate">{jc.party_snapshot?.name}</div>
                      <div className="text-[11px] text-[#64748B] font-mono-data mt-0.5">
                        {jc.fields?.vehicle_no || jc.fields?.unit_type || jc.fields?.site_name || "—"} · {jc.job_date}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-data font-bold text-[#0F172A]">
                        {fmtMoney(jc.grand_total, active?.base_currency || "INR")}
                      </div>
                      <div className="mt-1"><StatusChip status={jc.status}/></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F1F5F9]">
                    <IndustryChip industry={jc.industry}/>
                    <div className="flex gap-3 items-center">
                      {!["closed", "invoiced", "cancelled"].includes(jc.status) && (
                        <button onClick={() => { setEditing(jc); setModalOpen(true); }} className="text-[#64748B] p-1" aria-label="Edit" data-testid={`jc-edit-m-${jc.id}`} style={{ minWidth: 32, minHeight: 32 }}>
                          <Edit3 size={16}/>
                        </button>
                      )}
                      <button onClick={() => downloadPdf(jc)} className="text-[#0F172A] p-1" aria-label="PDF" data-testid={`jc-pdf-m-${jc.id}`} style={{ minWidth: 32, minHeight: 32 }}>
                        <FileDown size={16}/>
                      </button>
                      <button onClick={() => sendWhatsApp(jc)} className="text-[#25D366] p-1" aria-label="WhatsApp" style={{ minWidth: 32, minHeight: 32 }}>
                        <MessageCircle size={16}/>
                      </button>
                      {["open", "in_progress"].includes(jc.status) && (
                        <button onClick={() => closeCard(jc)} className="text-[#16A34A] p-1" aria-label="Close" data-testid={`jc-close-m-${jc.id}`} style={{ minWidth: 32, minHeight: 32 }}>
                          <CheckCircle2 size={16}/>
                        </button>
                      )}
                      {jc.invoice_id && (
                        <a href={`/app/invoices/${jc.invoice_id}`} className="text-[#2563EB] text-[11px] font-semibold">
                          {jc.invoice_number || "View"}
                        </a>
                      )}
                      {!jc.invoice_id && (
                        <button onClick={() => del(jc)} className="text-[#DC2626] p-1" aria-label="Delete" style={{ minWidth: 32, minHeight: 32 }}>
                          <Trash2 size={16}/>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop / tablet table — visible >= md */}
            <table className="table-clean hidden md:table">
            <thead>
              <tr>
                <th>Number</th><th>Industry</th><th>Customer</th>
                <th>Vehicle / Unit / Site</th><th>Date</th>
                <th className="text-right">Total</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((jc) => (
                <tr key={jc.id} data-testid={`jc-row-${jc.id}`}>
                  <td className="font-mono-data font-medium text-[#0F172A]">{jc.number}</td>
                  <td><IndustryChip industry={jc.industry}/></td>
                  <td>{jc.party_snapshot?.name}</td>
                  <td className="text-xs text-[#64748B] font-mono-data">
                    {jc.fields?.vehicle_no || jc.fields?.unit_type || jc.fields?.site_name || "-"}
                  </td>
                  <td className="font-mono-data text-[#64748B]">{jc.job_date}</td>
                  <td className="text-right font-mono-data font-semibold">
                    {fmtMoney(jc.grand_total, active?.base_currency || "INR")}
                  </td>
                  <td><StatusChip status={jc.status}/></td>
                  <td>
                    <div className="flex gap-2 justify-end items-center">
                      {!["closed", "invoiced", "cancelled"].includes(jc.status) && (
                        <button onClick={() => { setEditing(jc); setModalOpen(true); }} className="text-[#64748B] hover:text-[#0F172A]" title="Edit" data-testid={`jc-edit-${jc.id}`}>
                          <Edit3 size={14}/>
                        </button>
                      )}
                      <button onClick={() => downloadPdf(jc)} className="text-[#0F172A] hover:text-[#FF5500]" title="Download PDF" data-testid={`jc-pdf-${jc.id}`}>
                        <FileDown size={14}/>
                      </button>
                      <button onClick={() => sendWhatsApp(jc)} className="text-[#25D366]" title="Send WhatsApp" data-testid={`jc-wa-${jc.id}`}>
                        <MessageCircle size={14}/>
                      </button>
                      {["open", "in_progress"].includes(jc.status) && (
                        <button onClick={() => closeCard(jc)} className="text-[#16A34A]" title="Close & invoice" data-testid={`jc-close-${jc.id}`}>
                          <CheckCircle2 size={14}/>
                        </button>
                      )}
                      {jc.invoice_id && (
                        <a href={`/app/invoices/${jc.invoice_id}`} className="text-[#2563EB] text-[11px] font-semibold" title="View invoice">
                          {jc.invoice_number || "Invoice"}
                        </a>
                      )}
                      {!jc.invoice_id && (
                        <button onClick={() => del(jc)} className="text-[#DC2626]" title="Delete" data-testid={`jc-del-${jc.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      <JobCardModal open={modalOpen} initial={editing} onClose={() => setModalOpen(false)} onSaved={load}/>
    </div>
  );
}
